/**
 * El período fiscal con el que se está trabajando, para toda la aplicación.
 *
 * Se pedía en cada pantalla: al filtrar egresos, al mirar el libro diario, al
 * generar un reporte, al crear una factura. Cinco sitios preguntando lo mismo,
 * y cada uno con su propio valor — se podía estar viendo el estado de
 * resultados de julio mientras la lista de egresos mostraba septiembre.
 *
 * Ahora se escoge una vez en el menú, junto a la sede.
 *
 * Una advertencia que sí vale la pena conservar: al CREAR un documento, la
 * fecha tiene consecuencias. Si el período activo no es el mes en curso, los
 * formularios lo dicen en vez de fechar en silencio hacia atrás.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const LLAVE = 'nara_erp_periodo_activo';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
               'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

export const periodoActual = () => new Date().toISOString().slice(0, 7);

/** «septiembre de 2026» — para leerlo, no para mandarlo a la API. */
export function enLetras(p) {
  if (!p || !/^\d{4}-\d{2}$/.test(p)) return p || '';
  const [a, m] = p.split('-');
  return `${MESES[Number(m) - 1]} de ${a}`;
}

const PeriodoCtx = createContext(null);

export function PeriodoProvider({ children }) {
  const [periodo, setPeriodoRaw] = useState(
    () => localStorage.getItem(LLAVE) || periodoActual());

  const setPeriodo = useCallback((p) => {
    const v = p || periodoActual();
    setPeriodoRaw(v);
    localStorage.setItem(LLAVE, v);
  }, []);

  const valor = useMemo(() => ({
    periodo,
    setPeriodo,
    enLetras: enLetras(periodo),
    esActual: periodo === periodoActual(),
    // Para los `params` de una consulta, sin repetir el objeto.
    params: { periodo },
    paramsFiscal: { periodo_fiscal: periodo },
  }), [periodo, setPeriodo]);

  return <PeriodoCtx.Provider value={valor}>{children}</PeriodoCtx.Provider>;
}

export function usePeriodo() {
  const ctx = useContext(PeriodoCtx);
  if (!ctx) throw new Error('usePeriodo debe usarse dentro de <PeriodoProvider>');
  return ctx;
}
